// Notifications Web Push côté app : abonnement de cet appareil et état.
//
// Sur iPhone, Apple ne les autorise que dans une app ajoutée à l'écran
// d'accueil et ouverte depuis son icône (iOS 16.4+). Dans un onglet Safari,
// PushManager n'existe pas.
import API_CONFIG from '../config/api';

export type EtatPush = 'non-supporte' | 'ecran-accueil-requis' | 'refuse' | 'inactif' | 'actif';

function iphoneHorsApp(): boolean {
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const installee =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return ios && !installee;
}

async function enregistrement(): Promise<ServiceWorkerRegistration | undefined> {
  if (!('serviceWorker' in navigator)) return undefined;
  return navigator.serviceWorker.getRegistration();
}

export async function etatPush(): Promise<EtatPush> {
  if (!('PushManager' in window) || !('Notification' in window)) {
    return iphoneHorsApp() ? 'ecran-accueil-requis' : 'non-supporte';
  }
  if (Notification.permission === 'denied') return 'refuse';
  const reg = await enregistrement();
  if (!reg) return 'non-supporte';
  const abonnement = await reg.pushManager.getSubscription();
  return abonnement && Notification.permission === 'granted' ? 'actif' : 'inactif';
}

/** Adresse de notification de cet appareil, jointe aux demandes faites à Jarvis */
export async function endpointPushCourant(): Promise<string | undefined> {
  try {
    if (!('PushManager' in window)) return undefined;
    const reg = await enregistrement();
    const abonnement = await reg?.pushManager.getSubscription();
    return abonnement?.endpoint;
  } catch {
    return undefined;
  }
}

function base64UrlVersOctets(base64: string): Uint8Array<ArrayBuffer> {
  const complet = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const brut = atob(complet);
  const octets = new Uint8Array(new ArrayBuffer(brut.length));
  for (let i = 0; i < brut.length; i++) octets[i] = brut.charCodeAt(i);
  return octets;
}

async function postJson(url: string, corps: unknown) {
  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(corps),
  });
  return resp.json();
}

export async function activerPush(): Promise<void> {
  // Demandée en premier : iOS refuse une demande de permission qui n'arrive
  // pas directement après le tap de l'utilisateur.
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error(
      permission === 'denied'
        ? 'Notifications refusées : réactive-les dans Réglages → Notifications → Aquarius.'
        : 'Permission non accordée.'
    );
  }

  const reg = await enregistrement();
  if (!reg) throw new Error("Service worker indisponible : ferme et rouvre l'app, puis réessaie.");

  const cle = await (await fetch(API_CONFIG.endpoints.pushPublicKey)).json();
  if (!cle.success) throw new Error(cle.error || 'Clé de notification indisponible');

  // Un ancien abonnement a peut-être été fait avec une autre clé : on repart à neuf
  const ancien = await reg.pushManager.getSubscription();
  if (ancien) await ancien.unsubscribe();

  const abonnement = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: base64UrlVersOctets(cle.publicKey),
  });
  const json = abonnement.toJSON();

  const enregistre = await postJson(API_CONFIG.endpoints.pushSubscribe, {
    subscription: { endpoint: json.endpoint, keys: json.keys },
    appareil: navigator.userAgent,
  });
  if (!enregistre.success) throw new Error(enregistre.error || "Le serveur a refusé l'abonnement");

  // Notification de test : confirme tout de suite que la chaîne complète marche
  await postJson(API_CONFIG.endpoints.pushTest, { endpoint: json.endpoint }).catch(() => undefined);
}

export async function desactiverPush(): Promise<void> {
  const reg = await enregistrement();
  const abonnement = await reg?.pushManager.getSubscription();
  if (!abonnement) return;
  await postJson(API_CONFIG.endpoints.pushUnsubscribe, { endpoint: abonnement.endpoint }).catch(() => undefined);
  await abonnement.unsubscribe();
}
